/**
 * Frozen district registry for the /trends/[slug] static routes.
 *
 * Source of truth is `web-app/backend/app/data/district_slugs.py`,
 * which derives the slug list from `DISTRICT_CENTROIDS` in the
 * location resolver. Mirrored here so Next's `generateStaticParams`
 * can pre-render all ~67 pages at build time without fetching at
 * build step.
 *
 * Slugs are permanent once shipped (search engines index them).
 * Append new districts here; never rename existing entries.
 *
 * Display name is always the canonical AZ form. Search queries in
 * Russian frequently use the AZ spelling too ("квартиры Ясамал" is
 * rarer than "квартиры Yasamal"), so we pay the SEO juice to the
 * canonical name across locales instead of translating.
 */

export interface District {
  slug: string;
  /** Canonical AZ name - matches the DB key in listing_lifecycle /
   *  price_aggregates, and the value returned by
   *  district_summary.location. */
  name: string;
  lat: number;
  lng: number;
}

export const DISTRICTS: District[] = [
  { slug: "20-yanvar", name: "20 Yanvar", lat: 40.4018, lng: 49.8062 },
  { slug: "28-may", name: "28 May", lat: 40.3829, lng: 49.8487 },
  { slug: "7-ci-mikrorayon", name: "7-ci mikrorayon", lat: 40.4316, lng: 49.8542 },
  { slug: "8-ci-kilometr", name: "8-ci kilometr", lat: 40.4072, lng: 49.9553 },
  { slug: "8-ci-mikrorayon", name: "8-ci mikrorayon", lat: 40.4163, lng: 49.841 },
  { slug: "8-noyabr", name: "8 Noyabr", lat: 40.3984, lng: 49.8207 },
  { slug: "9-cu-mikrorayon", name: "9-cu mikrorayon", lat: 40.4243, lng: 49.8126 },
  { slug: "abseron", name: "Abşeron", lat: 40.4771, lng: 49.7591 },
  { slug: "ag-seher", name: "Ağ şəhər", lat: 40.3826, lng: 49.8885 },
  { slug: "avtovagzal", name: "Avtovağzal", lat: 40.4243, lng: 49.7979 },
  { slug: "azadliq-prospekti", name: "Azadlıq Prospekti", lat: 40.4243, lng: 49.8433 },
  { slug: "badamdar", name: "Badamdar", lat: 40.3438, lng: 49.8093 },
  { slug: "bakixanov", name: "Bakıxanov", lat: 40.4239, lng: 49.9604 },
  { slug: "bakmil", name: "Bakmil", lat: 40.4115, lng: 49.8736 },
  { slug: "bayil", name: "Bayıl", lat: 40.3409, lng: 49.832 },
  { slug: "bileceri", name: "Biləcəri", lat: 40.4354, lng: 49.7994 },
  { slug: "bine", name: "Binə", lat: 40.4513, lng: 50.0869 },
  { slug: "bineqedi", name: "Binəqədi", lat: 40.4353, lng: 49.8301 },
  { slug: "buzovna", name: "Buzovna", lat: 40.5228, lng: 50.1053 },
  { slug: "ceyranbatan", name: "Ceyranbatan", lat: 40.5407, lng: 49.6658 },
  { slug: "dernegul", name: "Dərnəgül", lat: 40.4244, lng: 49.8576 },
  { slug: "ehmedli", name: "Əhmədli", lat: 40.3817, lng: 49.9577 },
  { slug: "elmler-akademiyasi", name: "Elmlər Akademiyası", lat: 40.3771, lng: 49.813 },
  { slug: "emircan", name: "Əmircan", lat: 40.4229, lng: 49.9944 },
  { slug: "genclik", name: "Gənclik", lat: 40.3994, lng: 49.8535 },
  { slug: "gunesli", name: "Günəşli", lat: 40.3764, lng: 49.9763 },
  { slug: "hezi-aslanov", name: "Həzi Aslanov", lat: 40.3713, lng: 49.9544 },
  { slug: "hovsan", name: "Hövsan", lat: 40.3748, lng: 50.0785 },
  { slug: "iceri-seher", name: "İçəri Şəhər", lat: 40.3666, lng: 49.8282 },
  { slug: "insaatcilar", name: "İnşaatçılar", lat: 40.3908, lng: 49.8006 },
  { slug: "kohne-gunesli", name: "Köhnə Günəşli", lat: 40.3647, lng: 49.9683 },
  { slug: "koroglu", name: "Koroğlu", lat: 40.4215, lng: 49.9137 },
  { slug: "lokbatan", name: "Lökbatan", lat: 40.3211, lng: 49.7331 },
  { slug: "m-e-resulzade", name: "M.Ə.Rəsulzadə", lat: 40.4368, lng: 49.84 },
  { slug: "masazir", name: "Masazır", lat: 40.4878, lng: 49.7555 },
  { slug: "massiv-d", name: "Massiv D", lat: 40.3757, lng: 49.9798 },
  { slug: "massiv-v", name: "Massiv V", lat: 40.3828, lng: 49.9801 },
  { slug: "mehdiabad", name: "Mehdiabad", lat: 40.4912, lng: 49.8481 },
  { slug: "memar-ecemi", name: "Memar Əcəmi", lat: 40.4147, lng: 49.8141 },
  { slug: "memmedli", name: "Məmmədli", lat: 40.5124, lng: 49.9333 },
  { slug: "merdekan", name: "Mərdəkan", lat: 40.4866, lng: 50.1557 },
  { slug: "nardaran", name: "Nardaran", lat: 40.5865, lng: 49.9717 },
  { slug: "neftciler", name: "Neftçilər", lat: 40.4098, lng: 49.9447 },
  { slug: "neriman-nerimanov", name: "Nəriman Nərimanov", lat: 40.4029, lng: 49.871 },
  { slug: "nerimanov", name: "Nərimanov", lat: 40.3972, lng: 49.8514 },
  { slug: "nesimi", name: "Nəsimi", lat: 40.4011, lng: 49.8338 },
  { slug: "nizami", name: "Nizami", lat: 40.3847, lng: 49.8442 },
  { slug: "pirallahi", name: "Pirallahı", lat: 40.4664, lng: 50.3315 },
  { slug: "qara-qarayev", name: "Qara Qarayev", lat: 40.417, lng: 49.9337 },
  { slug: "qaracuxur", name: "Qaraçuxur", lat: 40.3989, lng: 49.9779 },
  { slug: "ramana", name: "Ramana", lat: 40.4685, lng: 49.9904 },
  { slug: "sabuncu", name: "Sabunçu", lat: 40.4664, lng: 49.9559 },
  { slug: "sah-ismayil-xetai", name: "Şah İsmayıl Xətai", lat: 40.3835, lng: 49.873 },
  { slug: "sahil", name: "Sahil", lat: 40.3731, lng: 49.8415 },
  { slug: "saray", name: "Saray", lat: 40.5382, lng: 49.7175 },
  { slug: "sea-breeze", name: "Sea Breeze", lat: 40.5877, lng: 49.9697 },
  { slug: "sebail", name: "Səbail", lat: 40.3464, lng: 49.8233 },
  { slug: "sixov", name: "Şıxov", lat: 40.306, lng: 49.7837 },
  { slug: "suraxani", name: "Suraxanı", lat: 40.3846, lng: 49.9818 },
  { slug: "ulduz", name: "Ulduz", lat: 40.4143, lng: 49.8948 },
  { slug: "xalqlar-dostlugu", name: "Xalqlar Dostluğu", lat: 40.3979, lng: 49.9536 },
  { slug: "xetai", name: "Xətai", lat: 40.369, lng: 49.9501 },
  { slug: "yasamal", name: "Yasamal", lat: 40.3828, lng: 49.8044 },
  { slug: "yeni-gunesli", name: "Yeni Günəşli", lat: 40.3796, lng: 49.9786 },
  { slug: "yeni-yasamal", name: "Yeni Yasamal", lat: 40.3914, lng: 49.7913 },
  { slug: "zabrat", name: "Zabrat", lat: 40.4865, lng: 49.9483 },
  { slug: "zig", name: "Zığ", lat: 40.3616, lng: 49.9851 },
];

const BY_SLUG: Record<string, District> = Object.fromEntries(
  DISTRICTS.map((d) => [d.slug, d]),
);

export function getDistrictBySlug(slug: string): District | undefined {
  return BY_SLUG[slug];
}

/**
 * Turn a district name string into its URL slug. Mirrors the
 * Python logic in `web-app/backend/app/services/district_slugs.py`
 * so a listing's server-reported district string can be matched
 * against the frozen DISTRICTS registry on the client.
 *
 * Folds AZ diacritics to ASCII (ə→e, ı→i, ü→u, ö→o, ğ→g, ş→s,
 * ç→c, İ→i), lowercases, collapses any non-[a-z0-9] run to a
 * single hyphen, and strips leading/trailing hyphens.
 */
const DIACRITIC_MAP: Record<string, string> = {
  "ə": "e", "Ə": "e",
  "ı": "i", "I": "i", "İ": "i",
  "ü": "u", "Ü": "u",
  "ö": "o", "Ö": "o",
  "ğ": "g", "Ğ": "g",
  "ş": "s", "Ş": "s",
  "ç": "c", "Ç": "c",
};

export function slugifyDistrict(name: string): string {
  const folded = name.replace(/[əƏıIİüÜöÖğĞşŞçÇ]/g, (ch) => DIACRITIC_MAP[ch] || ch);
  return folded
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/**
 * Resolve a raw district / location_name string (as returned by
 * the analyze API) to a District from the frozen registry.
 * Returns undefined for unknown / typoed inputs - callers MUST
 * check and skip the trend-page link rather than render a 404.
 */
export function resolveDistrictFromName(
  name: string | null | undefined,
): District | undefined {
  if (!name) return undefined;
  const slug = slugifyDistrict(name);
  if (!slug) return undefined;
  return BY_SLUG[slug];
}
